export const WIKI_ARTICLES = Object.freeze([
  {
    slug: 'daily-operations-checklist',
    title: 'Daily operations checklist',
    category: 'Operations',
    summary: 'A practical daily review list for pending clients, documents, payments, tickets, and exceptions.',
    tags: ['daily', 'checklist', 'operations', 'queue'],
    locales: ['en'],
    relatedRoutes: ['/dashboard', '/profiles', '/documents', '/transactions', '/support'],
    relatedHelpSlugs: ['dashboard', 'profiles', 'documents', 'transactions', 'support'],
  },
  {
    slug: 'register-and-verify-client',
    title: 'Register and verify a client',
    category: 'Client onboarding',
    summary: 'Review a new client profile, verify identity details, documents, and readiness for account operations.',
    tags: ['client', 'profile', 'verification', 'onboarding', 'kyc'],
    locales: ['en'],
    relatedRoutes: ['/profiles', '/documents'],
    relatedHelpSlugs: ['profiles', 'documents'],
  },
  {
    slug: 'process-client-document',
    title: 'Process a client document',
    category: 'Documents',
    summary: 'Review uploaded documents, add full translations when needed, and approve or reject the document.',
    tags: ['document', 'documents', 'review', 'approve', 'reject', 'translation', 'translation pending'],
    locales: ['en'],
    relatedRoutes: ['/documents', '/profiles'],
    relatedHelpSlugs: ['documents', 'profiles'],
  },
  {
    slug: 'open-or-sync-trading-account',
    title: 'Open or sync a trading account',
    category: 'Trading accounts',
    summary: 'Check client eligibility, open or sync trading accounts, and confirm platform account data.',
    tags: ['account', 'accounts', 'trading', 'sync', 'platform'],
    locales: ['en'],
    relatedRoutes: ['/accounts', '/profiles'],
    relatedHelpSlugs: ['accounts', 'profiles'],
  },
  {
    slug: 'approve-deposit',
    title: 'Approve a deposit',
    category: 'Payments',
    summary: 'Review a deposit transaction, verify supporting details, and approve the account funding flow.',
    tags: ['deposit', 'payment', 'transaction', 'approve'],
    locales: ['en'],
    relatedRoutes: ['/transactions', '/accounts', '/profiles'],
    relatedHelpSlugs: ['transactions', 'accounts', 'profiles'],
  },
  {
    slug: 'review-withdrawal',
    title: 'Review a withdrawal',
    category: 'Payments',
    summary: 'Review withdrawal requests, check client and account context, then approve or decline the request.',
    tags: ['withdrawal', 'payment', 'transaction', 'approve', 'decline', 'risk'],
    locales: ['en'],
    relatedRoutes: ['/transactions', '/accounts', '/profiles', '/documents'],
    relatedHelpSlugs: ['transactions', 'accounts', 'profiles', 'documents'],
  },
  {
    slug: 'resolve-support-ticket',
    title: 'Resolve a support ticket',
    category: 'Support',
    summary: 'Handle an open support ticket, review client context, respond, and close the case cleanly.',
    tags: ['support', 'ticket', 'client', 'message'],
    locales: ['en'],
    relatedRoutes: ['/support', '/profiles', '/internal-mail'],
    relatedHelpSlugs: ['support', 'profiles', 'internal-mail'],
  },
  {
    slug: 'investigate-client-activity',
    title: 'Investigate client activity',
    category: 'Client operations',
    summary: 'Use profile, document, account, transaction, and action log context to understand client activity.',
    tags: ['investigate', 'client', 'activity', 'audit', 'action log'],
    locales: ['en'],
    relatedRoutes: ['/profiles', '/documents', '/accounts', '/transactions', '/system/security/action-log'],
    relatedHelpSlugs: ['profiles', 'documents', 'accounts', 'transactions', 'system-security'],
  },
  {
    slug: 'check-partner-attribution',
    title: 'Check partner attribution',
    category: 'Partners',
    summary: 'Review client referral context, partner records, and commission-related attribution.',
    tags: ['partner', 'partners', 'attribution', 'referral', 'commission'],
    locales: ['en'],
    relatedRoutes: ['/partners', '/commissions', '/commission-plans', '/profiles'],
    relatedHelpSlugs: ['partners', 'commissions', 'commission-plans', 'profiles'],
  },
]);

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function tokenize(value) {
  return normalize(value).split(/[^a-z0-9]+/).filter(Boolean);
}

function routeMatches(route, pathname) {
  const normalizedRoute = String(route || '').replace(/\/+$/, '');
  const normalizedPathname = String(pathname || '').replace(/\/+$/, '');

  return normalizedPathname === normalizedRoute
    || normalizedPathname.startsWith(`${normalizedRoute}/`);
}

export function getWikiArticleBySlug(slug) {
  const normalizedSlug = normalize(slug);
  return WIKI_ARTICLES.find((article) => article.slug === normalizedSlug) || null;
}

export function getWikiArticlesByCategory(category) {
  const normalizedCategory = normalize(category);
  return WIKI_ARTICLES.filter((article) => normalize(article.category) === normalizedCategory);
}

export function getWikiArticlesForRoute(pathname) {
  return WIKI_ARTICLES.filter((article) => (
    article.relatedRoutes.some((route) => routeMatches(route, pathname))
  ));
}

export function searchWikiArticles(query) {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) {
    return WIKI_ARTICLES;
  }

  const queryTokens = tokenize(normalizedQuery);

  return WIKI_ARTICLES.filter((article) => {
    const searchableTokens = [
      article.title,
      article.summary,
      article.category,
      ...(article.tags || []),
    ].flatMap(tokenize);

    return queryTokens.every((token) => searchableTokens.includes(token));
  });
}
